<template>
    <span v-if="renderState" class="activity-generation-status-badge tw:inline-flex tw:items-center">
        <e-tooltip
            v-if="renderState === 'generating' && compact"
            label="Content is still being generated"
            position="is-left"
            active
        >
            <e-icon
                icon="circle-notch"
                spin
                class="tw:text-[var(--primary-color)]"
            />
        </e-tooltip>

        <e-tag
            v-else-if="renderState === 'generating'"
            type="is-info"
            class="has-text-weight-normal"
        >
            <e-icon icon="circle-notch" spin class="tw:mr-1" />
            Generating
        </e-tag>

        <e-tooltip
            v-else-if="renderState === 'failed' && compact"
            :label="failureLabel"
            position="is-left"
            type="is-danger"
            multilined
        >
            <e-icon
                icon="exclamation-triangle"
                class="tw:text-[var(--danger-color,#dc2626)]"
            />
        </e-tooltip>

        <e-tooltip
            v-else-if="renderState === 'failed'"
            :label="failureLabel"
            position="is-left"
            type="is-danger"
            multilined
        >
            <e-tag type="is-danger" class="has-text-weight-normal">
                <e-icon icon="exclamation-triangle" class="tw:mr-1" />
                Generation failed
            </e-tag>
        </e-tooltip>

        <e-tooltip
            v-else-if="renderState === 'completed' && compact"
            label="Content ready"
            position="is-left"
            type="is-success"
        >
            <e-icon icon="check" class="tw:text-[var(--success-color,#16a34a)]" />
        </e-tooltip>

        <e-tag
            v-else-if="renderState === 'completed'"
            type="is-success"
            class="has-text-weight-normal"
        >
            <e-icon icon="check" class="tw:mr-1" />
            Ready
        </e-tag>
    </span>
</template>

<script>
/**
 * Status badge for an activity (or activity template) whose AI-generated
 * content (slide deck, quiz, flashcard set, podcast) is still being prepared
 * by a Dispatch*GenerationsJob. Used in the Course Builder editor row
 * (ItemHeader.vue) and the student menu (nested-list-item/Activity.vue) so
 * both sides surface the same in-flight state.
 *
 * The "completed" pill is shown for ~3s after generation finishes (only when
 * showCompletedFlash is true and we observed the generating → not-generating
 * transition during the component's lifetime — not on first paint after a
 * page refresh, where the activity is simply ready).
 */
const FLASH_DURATION_MS = 3000;

export default {
    name: 'ActivityGenerationStatusBadge',

    props: {
        isGenerating: {
            type: Boolean,
            default: false,
        },
        generationStatus: {
            type: String,
            default: null,
        },
        generationError: {
            type: String,
            default: null,
        },
        compact: {
            type: Boolean,
            default: false,
        },
        showCompletedFlash: {
            type: Boolean,
            default: true,
        },
    },

    data: () => ({
        showingFlash: false,
        flashTimeoutId: null,
    }),

    computed: {
        renderState() {
            if (this.isGenerating) {
                return 'generating';
            }

            if (this.generationStatus === 'failed') {
                return 'failed';
            }

            if (this.showingFlash && this.generationStatus === 'completed') {
                return 'completed';
            }

            return null;
        },

        failureLabel() {
            return this.generationError || "We couldn't generate this content. You can retry from the activity.";
        },
    },

    watch: {
        isGenerating(next, prev) {
            if (prev === true && next === false && this.generationStatus === 'completed' && this.showCompletedFlash) {
                this.startFlash();
            }
        },
    },

    beforeUnmount() {
        if (this.flashTimeoutId) {
            clearTimeout(this.flashTimeoutId);
        }
    },

    methods: {
        startFlash() {
            this.showingFlash = true;

            if (this.flashTimeoutId) {
                clearTimeout(this.flashTimeoutId);
            }

            this.flashTimeoutId = setTimeout(() => {
                this.showingFlash = false;
                this.flashTimeoutId = null;
            }, FLASH_DURATION_MS);
        },
    },
};
</script>

<style scoped>
.activity-generation-status-badge {
    line-height: 1;
}
</style>
